// ?use_ios_ui — the DEPTH control: a vertical track whose knob follows the
// finger to either end, and a SPEED control rather than iOS's jog
// (dev, 2026-10-19: "if the thumb drags it all the way down the speed of the
// depth change should increase"). While held, the wheel moves every frame at
// a speed set by how far the knob is from centre — squared, so a small pull
// is a fine adjustment and the end of the track is the fastest. Down brings
// the wheel out, up pushes it into the well (negative depth), as on iOS. The
// knob returns to centre only on release.

import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react'
import { IOS_UI_PALETTE, PANEL_LABEL_STYLE, panelTileStyle } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import { SliderThumb } from '@/components/ios-ui/panel-slider/panel-slider'

/** Metres per second at full deflection. A feel value — tune on device. */
export const DEPTH_MAX_SPEED_MPS = 0.2

const THUMB_RADIUS_PX = 10

interface DepthSliderProps {
  onDrag: (delta: number) => void
  /** Injectable clock + frame scheduler (tests). Defaults: performance.now
   *  and a 16 ms timer — window.requestAnimationFrame is not dependable
   *  while an immersive-ar session owns the display. */
  now?: () => number
  scheduleFrame?: (callback: () => void) => void
}

export function DepthSlider({
  onDrag,
  now = () => performance.now(),
  scheduleFrame = (callback) => void setTimeout(callback, 16),
}: DepthSliderProps) {
  const [offset, setOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)
  const startY = useRef<number | null>(null)
  const offsetRef = useRef(0)
  const reachRef = useRef(1)
  const generation = useRef(0)
  const onDragRef = useRef(onDrag)
  onDragRef.current = onDrag

  // Every frame while held: speed from the knob's deflection, times the time
  // since the last frame. A new press or a release bumps the generation and
  // stops the loop that came before it.
  const runLoop = useCallback(() => {
    const mine = ++generation.current
    let last = now()
    const step = () => {
      if (mine !== generation.current) return
      const t = now()
      const fraction = offsetRef.current / reachRef.current
      onDragRef.current(Math.sign(fraction) * fraction * fraction * DEPTH_MAX_SPEED_MPS * ((t - last) / 1000))
      last = t
      scheduleFrame(step)
    }
    scheduleFrame(step)
  }, [now, scheduleFrame])

  useEffect(() => () => void (generation.current += 1), [])

  const handleDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const rect = trackRef.current?.getBoundingClientRect()
      reachRef.current = Math.max(1, (rect?.height ?? 0) / 2 - THUMB_RADIUS_PX)
      startY.current = e.clientY
      offsetRef.current = 0
      setOffset(0)
      setIsDragging(true)
      e.currentTarget.setPointerCapture?.(e.pointerId)
      runLoop()
    },
    [runLoop],
  )

  const handleMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (startY.current == null) return
    const reach = reachRef.current
    const next = Math.min(Math.max(e.clientY - startY.current, -reach), reach)
    offsetRef.current = next
    setOffset(next)
  }, [])

  const handleEnd = useCallback(() => {
    startY.current = null
    generation.current += 1
    offsetRef.current = 0
    setOffset(0)
    setIsDragging(false)
  }, [])

  return (
    <div
      data-testid="ios-depth-slider"
      role="slider"
      aria-label="Depth"
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleEnd}
      onPointerCancel={handleEnd}
      style={{
        ...panelTileStyle(isDragging),
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '14px 0 18px',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <span style={{ ...PANEL_LABEL_STYLE, color: isDragging ? IOS_UI_PALETTE.accentOnDark : IOS_UI_PALETTE.label }}>DEPTH</span>
      <div
        ref={trackRef}
        data-testid="ios-depth-track"
        style={{ position: 'relative', flex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}
      >
        <div style={{ width: 2, height: '100%', borderRadius: 1, background: IOS_UI_PALETTE.track }} />
        <div
          data-testid="ios-depth-thumb"
          data-offset={String(Math.round(offset))}
          style={{
            position: 'absolute',
            top: `calc(50% - ${THUMB_RADIUS_PX}px)`,
            transform: `translateY(${offset}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.34, 1.4, 0.64, 1)',
          }}
        >
          <SliderThumb />
        </div>
      </div>
    </div>
  )
}
